import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'

import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import useModal from '@Hooks/myhouse/useModal'
import MyhouseArticleImageResponse from '@Types/entities/MyhouseArticleImageResponse'
import useBoardRegister from '@Hooks/myhouse/board/useBoardRegister'
import usePrevious from '@Hooks/common/usePrevious'
import { ARTICLE_IMAGE_ACCEPT_TYPE } from '@Root/contants/myhouseRegister'
import { BOARD_IMAGE_MAX_COUNT, BOARD_IMAGE_MAX_MB_SIZE } from '@Root/contants/myhouse'

export default function useBoardImageRegister() {
  const { loadImageRecognition, reduxBoardRegister, setTitle } = useBoardRegister()
  const { open } = useModal()

  const [files, setFiles] = useState<File[]>([])
  const [previewURLs, setPreviewURLs] = useState<any[]>([])
  const [originImages, setOriginImages] = useState<MyhouseArticleImageResponse[]>([])

  const curImagesLength = originImages?.length + files?.length || 0
  const prevImagesLength = usePrevious(originImages?.length + files?.length || 0)

  const getFilesSize = (fs: File[]) => {
    return fs.reduce((acc: number, cur: File) => acc + cur.size, 0)
  }

  const validateFile = (file: File, files: File[], validateFileCount: number) => {
    if (!ARTICLE_IMAGE_ACCEPT_TYPE.split(', ').includes(file.type)) {
      open(MODAL_TYPE.UNATTACHABLE_EXTENSION)
      return false
    }

    if (files.length + originImages.length + 1 > BOARD_IMAGE_MAX_COUNT) {
      if (validateFileCount > 1) {
        open(MODAL_TYPE.MAX_PHOTO_COUNT_OVERFLOW)
      } else {
        open(MODAL_TYPE.MAX_PHOTO_COUNT)
      }
      return false
    }

    const addingFilesSize = file.size
    const currentFilesSize = getFilesSize(files)
    const originImagesSize = originImages.reduce((acc, cur) => acc + cur.sizeKB * 1024, 0)

    if (addingFilesSize + currentFilesSize + originImagesSize > BOARD_IMAGE_MAX_MB_SIZE * 1024 * 1024) {
      if (validateFileCount > 1) {
        open(MODAL_TYPE.MAX_PHOTO_SIZE_OVERFLOW)
      } else {
        open(MODAL_TYPE.MAX_PHOTO_SIZE)
      }
      return false
    }

    return true
  }

  const getFileArray = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length <= 0) {
      return []
    }

    const arr = [] as File[]
    for (let i = 0; i < event.target.files.length; i++) {
      arr.push(event.target.files[i])
    }

    return arr
  }

  const handleImagePromise = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fs = getFileArray(event)

    const filePromises = fs.map((file: File, i) => {
      // Return a promise per file
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        const previewImage: any = []

        reader.onloadend = async () => {
          try {
            reader.result && previewImage.push(reader.result)
            resolve(previewImage)
          } catch (err) {
            reject(err)
          }
        }

        reader.onerror = (error) => {
          reject(error)
        }
        reader.readAsDataURL(file)
      })
    })

    // Wait for all promises to be resolved
    const fileInfos = await Promise.all(filePromises)

    // Profit
    return fileInfos
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fs = getFileArray(event)

      handleImagePromise(event).then((response: any) => {
        const resultPreviewURLs = [...previewURLs]
        const resultFiles = [...files]

        for (let i = 0; i < fs.length; i++) {
          if (!validateFile(fs[i], resultFiles, fs.length)) {
            continue
          }
          resultPreviewURLs.push(response[i])
          resultFiles.push(fs[i])
        }
        setPreviewURLs(resultPreviewURLs)
        setFiles(resultFiles)
      })
    }
    event.target.value = ''
  }

  const handleRemoveClick = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    deleteImage(index)
  }

  const getDeletedArray = (arr: any[], index: number) => {
    return [...arr.slice(0, index), ...arr.slice(index + 1)]
  }

  const deleteImage = (index: number) => {
    if (index < originImages.length) {
      setOriginImages(getDeletedArray(originImages, index))
      return
    }

    const fileIndex = index - originImages.length
    setFiles(getDeletedArray(files, fileIndex))
    setPreviewURLs(getDeletedArray(previewURLs, fileIndex))
  }

  const getPreviewImageUrls = () => {
    const imageList = originImages?.map((element) => element.url) || []
    return [...imageList, ...previewURLs]
  }

  const getFileSize = () => {
    const originImagesSize = originImages?.reduce((acc, cur) => acc + cur.sizeKB * 1024, 0) || 0
    const currentFilesSize = getFilesSize(files)
    return currentFilesSize + originImagesSize
  }

  useEffect(() => {
    if (prevImagesLength === 0 && reduxBoardRegister.bbsTypeCode === 'NOTICE' && isEmpty(reduxBoardRegister.title)) {
      loadImageRecognition(files[0])
    }
    if (prevImagesLength !== 0 && curImagesLength === 0 && reduxBoardRegister.bbsTypeCode === 'NOTICE') {
      setTitle('')
    }
  }, [files.length, originImages?.length])

  return {
    files,
    previewURLs,
    originImages,
    setOriginImages,
    getPreviewImageUrls,
    getFileSize,
    handleImageChange,
    handleRemoveClick,
  }
}
